import { escapeHtml } from '../inventoree/format'
import { lastActivityHtml } from '../inventoree/columns'
import type { TableColumn } from '../ui/DataTable'
import type { IncidentStatus } from '../api/securitee'
import { deviceCellHtml } from './deviceLinks'
import { gzCellHtml } from './gravityzone/gzColumn'
import type { GzStatus } from '../api/securiteeGravityZone'

/** O coloana cu numele de ordonare pe server (`columns[i][name]`), ca in inventoree/columns.ts. */
export type Column = TableColumn & { name?: string }

/** `DevicesModel` si `EventsModel` din public/js/securitee.js, coloana cu coloana. */

const DASH = '<i class="fas fa-minus font-10"></i>'

function textOrDash(value: unknown): string {
  return value ? escapeHtml(value) : DASH
}

function ok(title: string): string {
  return `<center><i class="fas fa-check-circle font-18 color-green" data-placement="top" data-toggle="tooltip" data-original-title="${title}"> </i></center>`
}

function bad(title: string, cls = 'color-red'): string {
  return `<center><i class="fas fa-minus-circle font-18 ${cls}" data-placement="top" data-toggle="tooltip" data-original-title="${title}"> </i></center>`
}

/** Pornit, dar in urma: galben, ca gravitatea Major din alarme. */
function behind(title: string): string {
  return `<center><i class="fas fa-exclamation-circle font-18 color-yellow" data-placement="top" data-toggle="tooltip" data-original-title="${title}"> </i></center>`
}

/** NU STIU. Semnul de intrebare stins, ca la ce n-a raportat agentul inca. */
function unreported(title: string): string {
  return `<center><i class="fas fa-question-circle font-18 text-muted" data-placement="top" data-toggle="tooltip" data-original-title="${title}"> </i></center>`
}

export function deviceColumns(gravityZone = false): Column[] {
  const columns: Column[] = [
    { index: 0, title: '', orderable: false, visible: true, width: 50, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${r.ID}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Type', name: 'Tip', orderable: true, visible: true, width: 100, html: (r) => `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>` },
    {
      index: 2, title: 'Device name', name: 'Denumire', orderable: true, visible: true,
      html: (r) => `<p>${escapeHtml(r.Denumire)}</p>` + (Number(r.ownerId) > 0
        ? `<span class="color-light-grey"><i class="fa fa-user"></i> ${escapeHtml(r.Prenume)} ${escapeHtml(r.Nume)}</span>`
        : '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>'),
    },
    { index: 3, title: 'Last User Logged On', name: 'UserLogat', orderable: true, visible: true, html: (r) => textOrDash(r.UserLogat) },
    { index: 4, title: 'Group', name: 'DeviceGroup', orderable: true, visible: true, html: (r) => textOrDash(r.DeviceGroup) },
    { index: 5, title: 'Status', name: 'USB', orderable: true, visible: true, html: (r) => (Number(r.USB) > 0 ? ok('Module Enabled') : bad('Module Disabled')) },
    {
      /*
       * AV Status: ce vede Windows Security Center, urcat de agent in
       * `it_opteemum_security_software_status`. PATRU stari, nu trei - a patra e
       * "nu stiu", si e tot rostul indreptarii din 24 septembrie 2026: pana
       * atunci coloana citea tabelele prototipului Bitdefender, pe care nu le mai
       * umple nimeni, si spunea "Antivirus uninstalled" pe toata flota.
       *
       * Balonul poarta si produsele vazute pe masina, cand sunt - ca sa se poata
       * verifica ce anume a fost citit.
       */
      index: 6, title: 'AV Status', name: 'AntivirusStatus', orderable: true, visible: true,
      html: (r) => {
        const seen = r.AntivirusName ? `: ${escapeHtml(r.AntivirusName)}` : ''
        if (r.Antivirus === 'on') return ok(`Antivirus enabled${seen}`)
        if (r.Antivirus === 'stale') return behind(`Antivirus on, but behind${seen}`)
        if (r.Antivirus === 'off') return bad(`Antivirus switched off${seen}`, 'color-red-sunglo')
        return unreported('Antivirus not reported by the agent')
      },
    },
    { index: 7, title: 'Files', name: 'Files', orderable: true, visible: true, html: (r) => String(r.Files ?? 0) },
    { index: 8, title: 'Files size', name: 'FilesSize', orderable: true, visible: true, html: (r) => escapeHtml(r.FilesSize) },
    { index: 9, title: '', titleHtml: '<center><input type="checkbox" class="allDevices"/></center>', name: 'ID', orderable: false, visible: true, width: 50, html: (r) => `<center><input value="${r.ID}" type="checkbox" class="oneDevice"${r.__checked ? ' checked' : ''}/></center>` },
    { index: 10, title: 'Last Activity', name: 'DataAccesareDM', orderable: true, visible: true, html: (r) => lastActivityHtml(r.DataAccesareDM) },
  ]
  /* GravityZone (Bitdefender), numai la firmele legate: langa AV Status, cu index nou ca sa nu se mute ordonarea celorlalte. */
  if (gravityZone) {
    columns.splice(7, 0, { index: 11, title: 'GravityZone', orderable: false, visible: true, html: (r) => gzCellHtml(r.GravityZone as GzStatus | undefined) })
  }
  return columns
}

/** Securitee > EDR events: telemetria bruta (`it_edr_events`), cu descrierea pe o linie. */
export function edrEventColumns(allCompanies = false): Column[] {
  return [
    { index: 0, title: 'Time (UTC)', name: 'event_time', orderable: true, visible: true, width: 160, html: (r) => escapeHtml(r.event_time) },
    { index: 1, title: 'Device', name: 'device_name', orderable: true, visible: true, html: (r) => deviceCellHtml(r.device_id, r.device_name, String(r.event_time ?? '')) },
    companyColumn(5, allCompanies),
    { index: 2, title: 'Channel', name: 'channel', orderable: true, visible: true, width: 120, html: (r) => textOrDash(r.channel) },
    { index: 3, title: 'Event ID', name: 'event_id', orderable: true, visible: true, width: 90, html: (r) => escapeHtml(r.event_id) },
    { index: 4, title: 'Event', orderable: false, visible: true, html: (r) => textOrDash(r.text) },
  ]
}

export function eventColumns(): Column[] {
  return [
    { index: 0, title: 'Type', name: 'device_type', orderable: true, visible: true, width: 100, html: (r) => `<center style="cursor:default" data-toggle="tooltip" data-placement="top" title="${escapeHtml(r.device_type)}">${r.device_icon}</center>` },
    {
      index: 1, title: 'Device name', name: 'device_name', orderable: true, visible: true,
      html: (r) => `<p>${escapeHtml(r.device_name)}</p>` + (r.OwnerID ? '' : '<span class="color-red"><i class="fa fa-user"></i> Unassigned</span>'),
    },
    { index: 2, title: 'Logged User', name: 'username', orderable: true, visible: true, html: (r) => textOrDash(r.username) },
    { index: 3, title: 'File name', name: 'file_name', orderable: true, visible: true, html: (r) => escapeHtml(r.file_name) },
    { index: 4, title: 'File Destination', name: 'file_destination', orderable: true, visible: true, html: (r) => escapeHtml(r.file_destination) },
    { index: 5, title: 'Destination Source', name: 'file_source', orderable: true, visible: true, html: (r) => escapeHtml(r.file_source) },
    { index: 6, title: 'Device Date', name: 'device_date', orderable: true, visible: true, html: (r) => escapeHtml(r.device_date) },
    { index: 7, title: 'Date', name: 'server_date', orderable: true, visible: true, html: (r) => escapeHtml(r.server_date) },
  ]
}

/** Gravitatea ca eticheta Bootstrap, aceleasi culori ca restul portalului. */
const SEVERITY: Record<string, [string, string]> = {
  critical: ['Critical', 'label-danger'],
  high: ['High', 'label-warning'],
  medium: ['Medium', 'label-info'],
  low: ['Low', 'label-default'],
}

/** Textul si clasa etichetei de gravitate; necunoscuta = Low. */
export function severityLabel(severity: string): readonly [string, string] {
  return SEVERITY[severity] ?? SEVERITY.low!
}

/** Eticheta de gravitate a unei detectii - ecranul general si fila aparatului. */
export function severityHtml(severity: unknown): string {
  const [label, cls] = severityLabel(String(severity))
  return `<span class="label ${cls}">${label}</span>`
}

/** MITRE cu tactica, gol cand regula n-are. */
export function mitreHtml(r: Record<string, unknown>): string {
  return r.mitre ? `${escapeHtml(r.mitre)} <span class="font-grey-cascade">${escapeHtml(r.tactic)}</span>` : ''
}

/** Titlul detectiei, cu legatura spre incidentul ei, cand a intrat intr-unul. */
function detectionTitleHtml(r: Record<string, unknown>): string {
  const id = Number(r.incident_id ?? 0)
  const link = id > 0 ? `<div><a class="xdr-detection-incident" data-spa href="/app/securitee/incidents?incident=${id}">Incident #${id}</a></div>` : ''
  return escapeHtml(r.title) + link
}

/** Securitee > Detections - ordinea coloanelor = `Detections::COLUMNS` (API). */
export function detectionColumns(allCompanies = false): Column[] {
  return [
    { index: 0, title: 'Date (UTC)', name: 'detected_at', orderable: true, visible: true, width: 160, html: (r) => escapeHtml(r.detected_at) },
    { index: 1, title: 'Device', name: 'device_name', orderable: true, visible: true, html: (r) => deviceCellHtml(r.device_id, r.device_name, String(r.detected_at ?? '')) },
    companyColumn(6, allCompanies),
    {
      index: 2, title: 'Severity', name: 'rule_level', orderable: true, visible: true, width: 100,
      html: (r) => severityHtml(r.severity),
    },
    { index: 3, title: 'Detection', name: 'title', orderable: true, visible: true, html: detectionTitleHtml },
    {
      index: 4, title: 'MITRE ATT&CK', orderable: false, visible: true,
      html: mitreHtml,
    },
    { index: 5, title: 'Rule', name: 'rule_id', orderable: true, visible: true, width: 130, html: (r) => `${escapeHtml(r.rule_id)} (level ${Number(r.rule_level)})` },
  ]
}

const INCIDENT_STATUS = {
  open: ['Open', 'label-danger'],
  closed: ['Closed', 'label-success'],
} satisfies Record<IncidentStatus, readonly [string, string]>

/** Starea incidentului (a tichetului lui Easeedesk), cu culoarea ei. */
export function incidentStatusHtml(status: IncidentStatus): string {
  const [label, cls] = INCIDENT_STATUS[status]

  return `<span class="label ${cls}">${escapeHtml(label)}</span>`
}

/** Omul tichetului ("Ana Pop"); tichet deschis fara om = Unassigned; inchis fara om = nimic. */
export function ticketOwnerHtml(owner: string, open: boolean): string {
  if (owner !== '') return `<span class="incident-ticket-owner">${escapeHtml(owner)}</span>`

  return open ? '<span class="incident-ticket-owner incident-ticket-unassigned">Unassigned</span>' : ''
}

/** Tichetul Easeedesk al incidentului, ca legatura: numarul, starea si omul lui; fara tichet, nimic. */
export function incidentTicketHtml(id: number | null, status: string, missing = false, why = '', owner = '', open = false): string {
  if (missing) return `<span class="label label-danger incident-ticket-missing" title="${escapeHtml(why)}">Not opened</span>`
  if (id === null || id < 1) return ''

  const who = ticketOwnerHtml(owner, open)
  return `<a href="/app/easeedesk/ticket/${id}">#${id} ${escapeHtml(status)}</a>${who === '' ? '' : ' ' + who}`
}

/** Securitee > Incidents - numele de ordonare = `Incidents::COLUMNS` (API). */
/** Coloana "Company" a cozii SOC: numai pe "All companies" (docs XDR 03, decizia 7). */
function companyColumn(index: number, visible: boolean): Column {
  return { index, title: 'Company', orderable: false, visible, html: (r) => textOrDash(r.company_name) }
}

export function incidentColumns(allCompanies = false, canFalsePositive = false, canTicketFalsePositive = false): Column[] {
  return [
    { index: 0, title: '', orderable: false, visible: true, width: 40, html: (r) => `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(r.ID)}"><i class="fas fa-plus-circle"></i></center>` },
    { index: 1, title: 'Last detection (UTC)', name: 'last_detected_at', orderable: true, visible: true, width: 160, html: (r) => escapeHtml(r.last_detected_at) },
    { index: 2, title: 'Device', name: 'device_name', orderable: true, visible: true, html: (r) => deviceCellHtml(r.device_id, r.device_name, String(r.last_detected_at ?? '')) },
    companyColumn(8, allCompanies),
    { index: 3, title: 'Severity', name: 'max_level', orderable: true, visible: true, width: 100, html: (r) => severityHtml(r.severity) },
    { index: 4, title: 'Incident', name: 'title', orderable: true, visible: true, html: (r) => freshLabelHtml(r.__fresh) + escapeHtml(r.title), rowClass: (r) => (r.__fresh ? `incident-fresh incident-fresh-${String(r.__fresh)}` : undefined) },
    { index: 5, title: 'Detections', name: 'detections', orderable: true, visible: true, width: 100, html: (r) => String(Number(r.detections)) },
    { index: 6, title: 'Status', name: 'status', orderable: true, visible: true, width: 120, html: (r) => (r.false_positive === true ? '<span class="label label-default incident-false-positive">False positive</span>' : incidentStatusHtml(r.status === 'closed' ? 'closed' : 'open')) },
    { index: 7, title: 'Ticket', orderable: false, visible: true, width: 160, html: (r) => incidentTicketHtml(Number(r.ticket_id) > 0 ? Number(r.ticket_id) : null, String(r.ticket_status ?? ''), r.ticket_missing === true, String(r.ticket_error ?? ''), String(r.ticket_assignee ?? ''), r.ticket_open === true) },
    { index: 9, title: 'Actions', orderable: false, visible: true, width: canFalsePositive ? 270 : 170, html: (r) => incidentActionsHtml(r, canFalsePositive, canTicketFalsePositive) },
  ]
}

/** Butoanele din rand: Respond desface randul (`dd-toggle`), Ticket / Open ticket, si False positive pe cel deschis (cu tichet, numai cine poate muta tichete). */
export function incidentActionsHtml(r: Record<string, unknown>, canFalsePositive = false, canTicketFalsePositive = false): string {
  const ticket = Number(r.ticket_id)
  const second =
    ticket > 0
      ? `<a class="btn btn-xs btn-default incident-row-ticket" href="/app/easeedesk/ticket/${ticket}">Ticket</a>`
      : r.false_positive === true
        ? ''
        : `<button type="button" class="btn btn-xs btn-default incident-row-open-ticket" data-id="${Number(r.ID)}">Open ticket</button>`
  const third =
    canFalsePositive && (ticket < 1 || canTicketFalsePositive) && r.status === 'open' && r.false_positive !== true
      ? `<button type="button" class="btn btn-xs btn-default incident-row-false-positive" data-id="${Number(r.ID)}" data-device="${escapeHtml(String(r.device_name ?? ''))}" data-title="${escapeHtml(String(r.title ?? ''))}"${ticket > 0 ? ` data-ticket="${ticket}" data-fp-status="${escapeHtml(String(r.fp_ticket_status ?? ''))}"` : ''}>False positive</button>`
      : canFalsePositive && r.false_positive === true && typeof r.undo_until === 'string' && r.undo_until !== ''
        ? `<button type="button" class="btn btn-xs btn-default incident-row-undo" data-id="${Number(r.ID)}" title="Possible until ${escapeHtml(r.undo_until)} UTC">Undo</button>`
        : ''
  return `<span class="xdr-row-actions"><button type="button" class="btn btn-xs btn-danger dd-toggle incident-row-respond"><i class="fa fa-bolt" aria-hidden="true"></i> Respond</button>${second}${third}</span>`
}

/** Eticheta "New" / "Updated" a unui incident schimbat de la ultima privire (`useQueueFreshness`). */
function freshLabelHtml(fresh: unknown): string {
  if (fresh === 'new') return '<span class="incident-fresh-label incident-fresh-label-new">New</span>'
  if (fresh === 'updated') return '<span class="incident-fresh-label incident-fresh-label-updated">Updated</span>'
  return ''
}

